import { useEffect, useMemo, useRef, useState } from "react";
import { curveLinear, line, scaleLinear } from "d3";

const MARGIN = { top: 16, right: 16, bottom: 44, left: 56 };
const DEFAULT_HEIGHT = 320;
const MIN_MEASURED_WIDTH = 240;
const DOMAIN_PAD_FRACTION = 0.05;
const HOVER_RADIUS_PX = 30;

const AXIS_COLOR = "#9ca3af";
const TICK_TEXT_COLOR = "#6b7280";
const LABEL_COLOR = "#374151";
const ZERO_LINE_COLOR = "#c4c9d0";

const EMPHASIS_STROKE_WIDTH = 2.5;
const MUTED_STROKE_WIDTH = 1.5;
const MUTED_OPACITY = 0.4;

export interface XYPoint {
  x: number;
  y: number;
}

export interface XYSeries {
  key: string;
  label?: string;
  color: string;
  emphasis?: boolean;
  /** Drawn in array order — supports parametric loops (not a function of x). */
  points: XYPoint[];
}

export interface XYLinePlotProps {
  series: XYSeries[];
  xLabel: string;
  yLabel: string;
  width?: number;
  height?: number;
}

interface TooltipState {
  left: number;
  top: number;
  label: string;
  x: number;
  y: number;
}

const paddedDomain = (values: number[]): [number, number] => {
  if (!values.length) return [0, 1];
  let min = Infinity;
  let max = -Infinity;
  for (const value of values) {
    if (value < min) min = value;
    if (value > max) max = value;
  }
  const span = max - min || Math.abs(max) || 1;
  const pad = span * DOMAIN_PAD_FRACTION;
  return [min - pad, max + pad];
};

const formatValue = (value: number) =>
  Math.abs(value) >= 100 ? value.toFixed(0) : value.toFixed(2);

/**
 * Small parametric multi-series line plot. Points are connected in array
 * order (curveLinear), so closed loops (e.g. flow-volume) render correctly.
 */
export const XYLinePlot = ({
  series,
  xLabel,
  yLabel,
  width,
  height = DEFAULT_HEIGHT,
}: XYLinePlotProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [measuredWidth, setMeasuredWidth] = useState(MIN_MEASURED_WIDTH * 2);
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);

  useEffect(() => {
    if (width != null || !containerRef.current) return;
    const observer = new ResizeObserver(([entry]) => {
      setMeasuredWidth(Math.max(entry.contentRect.width, MIN_MEASURED_WIDTH));
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [width]);

  const plotWidth = width ?? measuredWidth;
  const innerWidth = Math.max(plotWidth - MARGIN.left - MARGIN.right, 10);
  const innerHeight = Math.max(height - MARGIN.top - MARGIN.bottom, 10);

  const { xScale, yScale, paths } = useMemo(() => {
    const allX = series.flatMap((s) => s.points.map((p) => p.x));
    const allY = series.flatMap((s) => s.points.map((p) => p.y));

    const xs = scaleLinear().domain(paddedDomain(allX)).range([0, innerWidth]);
    const ys = scaleLinear().domain(paddedDomain(allY)).range([innerHeight, 0]);

    const generator = line<XYPoint>()
      .x((p) => xs(p.x))
      .y((p) => ys(p.y))
      .curve(curveLinear);

    const seriesPaths = series.map((s) => ({
      series: s,
      d: generator(s.points) ?? "",
    }));

    return { xScale: xs, yScale: ys, paths: seriesPaths };
  }, [series, innerWidth, innerHeight]);

  const xDomain = xScale.domain();
  const yDomain = yScale.domain();
  const showZeroX = xDomain[0] < 0 && xDomain[1] > 0;
  const showZeroY = yDomain[0] < 0 && yDomain[1] > 0;
  const xTicks = xScale.ticks(6);
  const yTicks = yScale.ticks(5);

  const handleMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const mx = e.clientX - rect.left - MARGIN.left;
    const my = e.clientY - rect.top - MARGIN.top;

    let best: TooltipState | null = null;
    let bestDist = HOVER_RADIUS_PX * HOVER_RADIUS_PX;
    for (const s of series) {
      for (const point of s.points) {
        const dx = xScale(point.x) - mx;
        const dy = yScale(point.y) - my;
        const dist = dx * dx + dy * dy;
        if (dist < bestDist) {
          bestDist = dist;
          best = {
            left: xScale(point.x) + MARGIN.left,
            top: yScale(point.y) + MARGIN.top,
            label: s.label ?? s.key,
            x: point.x,
            y: point.y,
          };
        }
      }
    }
    setTooltip(best);
  };

  return (
    <div ref={containerRef} className="relative w-full">
      <svg
        width={plotWidth}
        height={height}
        className="block"
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setTooltip(null)}
      >
        <g transform={`translate(${MARGIN.left},${MARGIN.top})`}>
          {showZeroX && (
            <line
              x1={xScale(0)}
              x2={xScale(0)}
              y1={0}
              y2={innerHeight}
              stroke={ZERO_LINE_COLOR}
              strokeDasharray="4,3"
            />
          )}
          {showZeroY && (
            <line
              x1={0}
              x2={innerWidth}
              y1={yScale(0)}
              y2={yScale(0)}
              stroke={ZERO_LINE_COLOR}
              strokeDasharray="4,3"
            />
          )}

          {/* X axis */}
          <g transform={`translate(0,${innerHeight})`}>
            <line x1={0} x2={innerWidth} stroke={AXIS_COLOR} />
            {xTicks.map((tick) => (
              <g key={tick} transform={`translate(${xScale(tick)},0)`}>
                <line y2={4} stroke={AXIS_COLOR} />
                <text
                  y={16}
                  textAnchor="middle"
                  fontSize={10}
                  fill={TICK_TEXT_COLOR}
                >
                  {tick}
                </text>
              </g>
            ))}
            <text
              x={innerWidth / 2}
              y={38}
              textAnchor="middle"
              fontSize={11}
              fill={LABEL_COLOR}
            >
              {xLabel}
            </text>
          </g>

          {/* Y axis */}
          <g>
            <line y1={0} y2={innerHeight} stroke={AXIS_COLOR} />
            {yTicks.map((tick) => (
              <g key={tick} transform={`translate(0,${yScale(tick)})`}>
                <line x2={-4} stroke={AXIS_COLOR} />
                <text
                  x={-8}
                  textAnchor="end"
                  dominantBaseline="middle"
                  fontSize={10}
                  fill={TICK_TEXT_COLOR}
                >
                  {tick}
                </text>
              </g>
            ))}
            <text
              transform={`translate(-44,${innerHeight / 2}) rotate(-90)`}
              textAnchor="middle"
              fontSize={11}
              fill={LABEL_COLOR}
            >
              {yLabel}
            </text>
          </g>

          {/* Series (array order, muted first so emphasized draw on top) */}
          {paths
            .slice()
            .sort(
              (a, b) =>
                Number(a.series.emphasis ?? false) -
                Number(b.series.emphasis ?? false),
            )
            .map(({ series: s, d }) => (
              <path
                key={s.key}
                d={d}
                fill="none"
                stroke={s.color}
                strokeWidth={
                  s.emphasis ? EMPHASIS_STROKE_WIDTH : MUTED_STROKE_WIDTH
                }
                strokeOpacity={s.emphasis ? 1 : MUTED_OPACITY}
              />
            ))}

          {tooltip && (
            <circle
              cx={tooltip.left - MARGIN.left}
              cy={tooltip.top - MARGIN.top}
              r={3.5}
              fill="white"
              stroke={LABEL_COLOR}
              strokeWidth={1.5}
              pointerEvents="none"
            />
          )}
        </g>
      </svg>

      {tooltip && (
        <div
          className="absolute pointer-events-none z-10 bg-gray-800 text-white text-[11px] rounded px-2 py-1 whitespace-nowrap"
          style={{
            left: tooltip.left + 10,
            top: tooltip.top - 12,
            transform:
              tooltip.left > plotWidth * 0.75 ? "translateX(-120%)" : undefined,
          }}
        >
          <span className="font-semibold">{tooltip.label}</span>
          <span className="ml-1">
            {xLabel}: {formatValue(tooltip.x)} · {yLabel}:{" "}
            {formatValue(tooltip.y)}
          </span>
        </div>
      )}
    </div>
  );
};
